import React, { useContext } from 'react';

import { DesignerLevel as Level, DesignerSource, PenaltyState } from '__generated__/types';
import { isDesignerOnline } from 'projects/util/designer';
import AddDesignerButton from './AddDesignerButton';
import Avatar from './Avatar';
import BriefContext from './BriefContext';
import LocalTime from './LocalTime';
import OnlineStatus from './OnlineStatus';
import PillContainer from './PillContainer';

export type Designer = {
    id: number;
    displayName: string;
    penaltyState: PenaltyState;
    profile: {
        utcOffset: string;
        lastSeenAt: any;
        designs: { image: string; original: string; title: string }[];
        designerLevel: Level;
        certifications: string[];
        locales: {
            tag: string;
            title: string;
        }[];
        biography: string;
        categories: {
            key: string;
            title: string;
        }[];
        responsivenessScore: number;
    };
};

type Props = {
    designer: Designer;
    onAddDesigner?: () => void;
};

const DesignerDetails: React.FunctionComponent<Props> = props => {
    const { designer, onAddDesigner } = props;
    const isOnlineNow = isDesignerOnline(designer);

    const { biography, locales, certifications, categories } = designer.profile;
    const isSquarespaceCertified = certifications.some(
        certification => certification === 'squarespace'
    );

    const { brief, totalAllowableDesigners } = useContext(BriefContext);

    return (
        <div className="designer-details">
            <div className="designer-details__panel designer-details__panel--with-separator">
                <div className="designer-details__meta">
                    <div className="designer-details__meta__avatar">
                        <Avatar userId={designer.id} variations={['small']} />
                    </div>
                    <div className="designer-details__meta__details">
                        <div className="paragraph paragraph--bold paragraph--text paragraph--regular paragraph--no-margin">
                            {designer.displayName}
                        </div>
                        <div className="paragraph paragraph--small paragraph--no-margin">
                            <OnlineStatus
                                isOnline={isOnlineNow}
                                lastSeenAt={designer.profile.lastSeenAt}
                            />
                        </div>
                        <div className="paragraph paragraph--small paragraph--no-margin">
                            <LocalTime utcOffset={designer.profile.utcOffset} />
                        </div>
                    </div>
                </div>
                <AddDesignerButton
                    designer={designer}
                    designerSource={DesignerSource.RECOMMENDED}
                    onAddDesigner={onAddDesigner}
                    brief={brief}
                    totalAllowableDesigners={totalAllowableDesigners}
                />
            </div>
            <div className="designer-details__panel">
                <div className="designer-details__description paragraph paragraph--small">
                    {biography}
                </div>
                {locales.length > 0 && (
                    <PillContainer
                        title={__('Languages')}
                        items={locales.map(locale => locale.title)}
                    />
                )}
                {categories.length > 0 && (
                    <PillContainer
                        title={__('Categories')}
                        items={categories.map(category => category.title)}
                    />
                )}
                {isSquarespaceCertified && (
                    <PillContainer title={__('Certifications')} items={[__('Squarespace')]} />
                )}
            </div>
        </div>
    );
};

export default DesignerDetails;
